import { Flex, Spinner, Text } from '@radix-ui/themes'
import { MessageCallout } from '~/components/MessageCallout'
import type { StationLeakV0Series } from './chartModel'
import type { LeakV0Window } from './chartWindow'
import { LeakV0ChartToolbar } from './LeakV0ChartToolbar'
import { StationLeakV0Chart } from './StationLeakV0Chart'
import { useLeakV0Series } from './useLeakV0Series'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

interface StationLeakV0ChartSectionProps {
  stationId: string
  fetched: LeakV0Window
  zoom: LeakV0Window | null
  episodes: StationLeakV0Episode[]
  selectedEpisodeId: string | null
  onShift: (direction: -1 | 1) => void
  onSelectEpisode: (episodeId: string) => void
  onZoom: (range: LeakV0Window) => void
  onResetZoom: () => void
}

interface LeakV0ChartBodyProps {
  series: StationLeakV0Series | undefined
  errorMessage: string | null
  episodes: StationLeakV0Episode[]
  view: LeakV0Window
  onZoom: (range: LeakV0Window) => void
}

const LeakV0ChartBody = ({ series, errorMessage, episodes, view, onZoom }: LeakV0ChartBodyProps) => {
  if (errorMessage) return <MessageCallout color="red" message={`Chart unavailable · ${errorMessage}`} />

  if (!series) {
    return (
      <Flex align="center" gap="2">
        <Spinner size="2" />
        <Text size="2" color="gray">
          Loading buckets…
        </Text>
      </Flex>
    )
  }

  if (series.buckets.length === 0) {
    return (
      <Text size="2" color="gray">
        No detector buckets in this window.
      </Text>
    )
  }

  return <StationLeakV0Chart series={series} episodes={episodes} from={view.from} to={view.to} onZoom={onZoom} />
}

export const StationLeakV0ChartSection = ({
  stationId,
  fetched,
  zoom,
  episodes,
  selectedEpisodeId,
  onShift,
  onSelectEpisode,
  onZoom,
  onResetZoom
}: StationLeakV0ChartSectionProps) => {
  const seriesQuery = useLeakV0Series(stationId, fetched)

  return (
    <Flex direction="column" gap="3" py="2">
      <LeakV0ChartToolbar
        fetched={fetched}
        zoom={zoom}
        episodes={episodes}
        selectedEpisodeId={selectedEpisodeId}
        fetching={seriesQuery.isFetching}
        onShift={onShift}
        onSelectEpisode={onSelectEpisode}
        onResetZoom={onResetZoom}
      />

      <LeakV0ChartBody
        series={seriesQuery.data}
        errorMessage={seriesQuery.error?.message ?? null}
        episodes={episodes}
        view={zoom ?? fetched}
        onZoom={onZoom}
      />
    </Flex>
  )
}
